import React, { useState, useContext, useEffect, useRef } from 'react';
import { CurpContext } from '../context/CurpContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import BackgroundLayout from '../components/BackgroundLayout';
import { toast } from 'react-hot-toast';

const SubstancePage = () => {
    const navigate = useNavigate();
    const buttonsContainer = "flex w-full space-x-4 mt-5";
    const inputClass = "border p-2 rounded-xl w-full drop-shadow-sm mt-1 bg-white/50 focus:bg-white focus:ring-2 focus:ring-[#f8485e]/20 outline-none transition-all text-gray-700 font-medium";
    const labelClass = "block text-gray-700 font-bold uppercase text-xs sm:text-sm tracking-wider mb-3 ml-1";
    const topRef = useRef(null);

    const { curp } = useContext(CurpContext);

    // WIZARD STATE
    const [currentStep, setCurrentStep] = useState(0);
    const totalSteps = 4;
    const [direction, setDirection] = useState(1);

    const [formData, setFormData] = useState({
        substance_user_frequency: '',
        substances: [],
        substances_affirmations: [],
        injected_last_month: null,
        needle_sharing_behavior: '',
        inhaled_last_month: null,
        inhalant_sharing_behavior: '',
        risk_reduction_knowledge: null,
    });

    const substancesList = [
        'Alcohol', 'Ayahuasca', 'Cannabis', 'Cocaína',
        'GHB', 'Alucinógenos', 'Heroína', 'Inhalantes',
        'MDMA', 'Metanfetamina', 'Mescalina', 'Psilocibina',
        'Opioides con receta médica', 'Opioides controlados sin receta', 'Tranquilizantes', 'Tabaco',
        'Poppers', 'Viagra', 'Cloruro de étilo', 'Ninguna'
    ];

    const substancesAffirmationsList = [
        'He inhalado alguna sustancia en el último mes',
        'He fumado alguna sustancia en el último mes',
        'He tomado alguna sustancia en el último mes',
        'Me he inyectado (o me han inyectado) alguna sustancia en el último mes',
        'Me he aplicado (o me han aplicado) alguna sustancias por vía rectal en el último mes',
        'Me he aplicado alguna sustancia vía ocular en el último mes',
        'Ninguna de las anteriores'
    ];

    // LOAD DRAFT
    useEffect(() => {
        const saved = localStorage.getItem('subsDraft');
        if (saved) {
            try {
                setFormData(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to parse draft");
            }
        }
    }, []);

    // SAVE DRAFT
    useEffect(() => {
        localStorage.setItem('subsDraft', JSON.stringify(formData));
    }, [formData]);

    const scrollToNextField = (nextFieldId) => {
        if (!nextFieldId) return;
        setTimeout(() => {
            const el = document.getElementById(nextFieldId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 300);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleButtonClick = (type, value) => {
        if (value === 'Ninguna' || value === 'Ninguna de las anteriores') {
            setFormData({ ...formData, [type]: [value] });
        } else {
            setFormData((prevState) => {
                const updatedList = prevState[type].includes(value)
                    ? prevState[type].filter((item) => item !== value)
                    : [...prevState[type], value];
                return {
                    ...prevState,
                    [type]: updatedList.filter(item => item !== 'Ninguna' && item !== 'Ninguna de las anteriores')
                };
            });
        }
    };

    const handleButtonClick2 = (name, response) => {
        if (name === "needle") {
            const isYes = response === 'yes';
            setFormData(prev => ({ 
                ...prev, 
                injected_last_month: isYes,
                needle_sharing_behavior: isYes ? prev.needle_sharing_behavior : ''
            }));
            if (isYes) scrollToNextField("field-needle-share");
        }

        if (name === "inhalant") {
            const isYes = response === 'yes';
            setFormData(prev => ({ 
                ...prev, 
                inhaled_last_month: isYes,
                inhalant_sharing_behavior: isYes ? prev.inhalant_sharing_behavior : ''
            }));
            if (isYes) scrollToNextField("field-inhalant-share");
        }

        if (name === "risk_reduction_knowledge") {
            setFormData(prev => ({ ...prev, risk_reduction_knowledge: response }));
        }
    };

    // VALIDATION
    const isStepValid = (stepIndex) => {
        switch (stepIndex) {
            case 0:
                return formData.substance_user_frequency !== '' && formData.substances.length > 0;
            case 1:
                return formData.substances_affirmations.length > 0;
            case 2:
                let valid2 = formData.injected_last_month !== null && formData.inhaled_last_month !== null;
                if (formData.injected_last_month === true && formData.needle_sharing_behavior === '') valid2 = false;
                if (formData.inhaled_last_month === true && formData.inhalant_sharing_behavior === '') valid2 = false;
                return valid2;
            case 3:
                return formData.risk_reduction_knowledge !== null;
            default:
                return false;
        }
    };

    // Auto-scroll
    useEffect(() => {
        if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    }, [currentStep]);

    const nextStep = () => {
        if (isStepValid(currentStep)) {
            setDirection(1);
            setCurrentStep((prev) => prev + 1);
        }
    };

    const prevStep = () => {
        setDirection(-1);
        setCurrentStep((prev) => prev - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isStepValid(3)) return;

        const dataToSend = { ...formData, curp: curp };

        try {
            const response = await fetch('https://admin-django-inspira.azurewebsites.net/api/services/register-substance/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSend)
            });

            if (response.status === 201) {
                localStorage.removeItem('subsDraft');
                toast.success('Formulario enviado con éxito.');
                toast.success('Genial, ¿Cuándo nos visitas? 😬');
                navigate('/services/counseling/substance/appointment');
            } else {
                toast.error('Ay, algo pasó, intenta nuevamente.');
            }
        } catch (error) {
            toast.error('Error de red, intenta nuevamente.');
        }
    };

    const variants = {
        enter: (direction) => ({ x: direction > 0 ? 1000 : -1000, opacity: 0 }),
        center: { zIndex: 1, x: 0, opacity: 1 },
        exit: (direction) => ({ zIndex: 0, x: direction < 0 ? 1000 : -1000, opacity: 0 })
    };

    const renderStep0 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f8485e] mt-3 drop-shadow-sm uppercase leading-tight text-center mb-6'>Frecuencia</motion.h1>
            <div className="mb-6">
                <label className={labelClass}>¿Sueles usar sustancias durante tus relaciones sexuales?</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-1">
                    {[
                        { value: "0", label: "Siempre" },
                        { value: "1", label: "A veces" },
                        { value: "2", label: "Nunca" }
                    ].map((opt) => (
                        <div key={opt.value} className="relative">
                            <motion.button type="button" whileTap={{ scale: 0.95 }}
                                onClick={() => setFormData(prev => ({ ...prev, substance_user_frequency: opt.value }))}
                                className={`w-full py-4 px-3 rounded-xl border-2 text-center font-bold uppercase tracking-widest transition-all text-xs ${formData.substance_user_frequency === opt.value ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'}`}
                            >
                                {opt.label}
                            </motion.button>
                        </div>
                    ))}
                </div>
            </div>
            <div className="mt-8">
                <label className={labelClass}>Selecciona las sustancias que hayas consumido en los últimos 6 meses (puedes seleccionar más de una):</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-1 mt-5">
                    {substancesList.map((substance) => (
                        <motion.button key={substance} type="button" whileTap={{ scale: 0.95 }} onClick={() => handleButtonClick('substances', substance)}
                            className={`w-full py-4 px-2 border-2 rounded-xl drop-shadow-sm items-center justify-center text-center text-[11px] font-bold uppercase tracking-wide transition-all ${
                                formData.substances.includes(substance) ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'
                            }`}
                        >
                            {substance}
                        </motion.button>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderStep1 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f8485e] mt-3 drop-shadow-sm uppercase leading-tight text-center mb-6'>Vías de consumo</motion.h1>
            <div className="mt-4">
                <label className={labelClass}>De las siguientes opciones, selecciona las afirmaciones que apliquen a tu caso (puedes elegir más de una):</label>
                <div className="flex flex-col gap-3 p-1 mt-5 w-full">
                    {substancesAffirmationsList.map((affirmation) => (
                        <motion.button key={affirmation} type="button" whileTap={{ scale: 0.95 }} onClick={() => handleButtonClick('substances_affirmations', affirmation)}
                            className={`w-full py-5 px-4 border-2 rounded-xl drop-shadow-sm text-left text-xs font-bold uppercase tracking-wide transition-all ${
                                formData.substances_affirmations.includes(affirmation) ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'
                            }`}
                        >
                            {affirmation}
                        </motion.button>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-center text-7xl font-bold text-[#f8485e] mt-2 mb-2 drop-shadow-sm leading-none'>⚡️</motion.h1>
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f8485e] mt-3 drop-shadow-sm uppercase leading-tight text-center mb-6'>Prácticas</motion.h1>
            
            <div className="mb-8">
                <label className={labelClass}>En el último mes ¿Te has inyectado alguna sustancia?</label>
                <div className={buttonsContainer}>
                    <motion.div className='w-1/2 flex justify-center' whileTap={{ scale: 0.95 }}>
                        <button type="button" onClick={() => handleButtonClick2('needle', 'yes')} 
                            className={`w-full rounded-xl border-2 py-4 text-center font-bold uppercase tracking-widest transition-all text-xs ${formData.injected_last_month === true ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'}`}>Sí</button>
                    </motion.div>
                    <motion.div className='w-1/2 flex justify-center' whileTap={{ scale: 0.95 }}>
                        <button type="button" onClick={() => handleButtonClick2('needle', 'no')} 
                            className={`w-full rounded-xl border-2 py-4 text-center font-bold uppercase tracking-widest transition-all text-xs ${formData.injected_last_month === false ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'}`}>No</button>
                    </motion.div>
                </div>
            </div>

            <AnimatePresence>
                {formData.injected_last_month && (
                    <motion.div id="field-needle-share" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-10 overflow-hidden">
                        <label className={labelClass}>¿Compartiste la aguja y las jeringas que utilizaste?</label>
                        <select name="needle_sharing_behavior" value={formData.needle_sharing_behavior} onChange={handleChange} className={inputClass} required>
                            <option value="">Selecciona</option>
                            <option value="0">Sí</option>
                            <option value="1">No</option>
                            <option value="2">A veces</option>
                        </select>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="mb-8">
                <label className={labelClass}>En el último mes ¿Has inhalado alguna sustancia?</label>
                <div className={buttonsContainer}>
                    <motion.div className='w-1/2 flex justify-center' whileTap={{ scale: 0.95 }}>
                        <button type="button" onClick={() => handleButtonClick2('inhalant', 'yes')} 
                            className={`w-full rounded-xl border-2 py-4 text-center font-bold uppercase tracking-widest transition-all text-xs ${formData.inhaled_last_month === true ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'}`}>Sí</button>
                    </motion.div>
                    <motion.div className='w-1/2 flex justify-center' whileTap={{ scale: 0.95 }}>
                        <button type="button" onClick={() => handleButtonClick2('inhalant', 'no')} 
                            className={`w-full rounded-xl border-2 py-4 text-center font-bold uppercase tracking-widest transition-all text-xs ${formData.inhaled_last_month === false ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'}`}>No</button>
                    </motion.div>
                </div>
            </div>

            <AnimatePresence>
                {formData.inhaled_last_month && (
                    <motion.div id="field-inhalant-share" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-4 overflow-hidden">
                        <label className={labelClass}>¿Compartiste los instrumentos que utilizaste para hacerlo con otras personas?</label>
                        <select name="inhalant_sharing_behavior" value={formData.inhalant_sharing_behavior} onChange={handleChange} className={inputClass} required>
                            <option value="">Selecciona</option>
                            <option value="0">Sí</option>
                            <option value="1">No</option>
                            <option value="2">A veces</option>
                        </select>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    const renderStep3 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f8485e] mt-3 drop-shadow-sm uppercase leading-tight text-center mb-6'>Reducción de Riesgos</motion.h1>
            <div className="mb-4">
                <label className={labelClass}>¿Conoces alguna estrategia de reducción de riesgos y mitigación de daños?</label>
                <div className={buttonsContainer}>
                    <motion.div className='w-1/2 flex justify-center' whileTap={{ scale: 0.95 }}>
                        <button type="button" onClick={() => handleButtonClick2('risk_reduction_knowledge', true)}
                            className={`w-full rounded-xl border-2 py-4 text-center font-bold uppercase tracking-widest transition-all text-xs ${formData.risk_reduction_knowledge === true ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'}`}>Sí</button>
                    </motion.div>
                    <motion.div className='w-1/2 flex justify-center' whileTap={{ scale: 0.95 }}>
                        <button type="button" onClick={() => handleButtonClick2('risk_reduction_knowledge', false)}
                            className={`w-full rounded-xl border-2 py-4 text-center font-bold uppercase tracking-widest transition-all text-xs ${formData.risk_reduction_knowledge === false ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'}`}>No</button>
                    </motion.div>
                </div>
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch(currentStep) {
            case 0: return renderStep0();
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            default: return null;
        }
    };

    return (
        <BackgroundLayout className="px-8 flex flex-col items-center pb-24">
            <div ref={topRef} className="absolute top-0 w-full h-1" />
            <Logo />
            <motion.div className="w-full lg:w-2/3">
                {currentStep === 0 && (
                    <>
                        <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.5 }} className='text-5xl font-bold text-[#f8485e] mt-10 uppercase drop-shadow-sm'>Ya casi acabamos 😅</motion.h1>
                        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-justify font-bold mt-5 mb-5 text-gray-500 uppercase drop-shadow-sm">Recuerda que la información que nos compartes es confidencial, está protegida y a nuestro equipo de consejería le sirve para brindarte un mejor servicio en reducción de riesgos y mitigación de daños.</motion.p>
                    </>
                )}

                <div className="w-full mt-6 mb-4">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center mb-2">Paso {currentStep + 1} de {totalSteps}</p>
                    <div className="w-full h-1.5 bg-white/50 rounded-full overflow-hidden">
                        <motion.div 
                            className="h-full bg-[#f8485e]"
                            initial={{ width: 0 }}
                            animate={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                            transition={{ duration: 0.3 }}
                        />
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="w-full mt-4 overflow-hidden">
                    <AnimatePresence mode="wait" custom={direction}>
                        <motion.div
                            key={currentStep}
                            custom={direction}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } }}
                        >
                            {renderCurrentStep()}
                        </motion.div>
                    </AnimatePresence>

                    <div className="flex justify-between gap-4 mt-8 mb-12">
                        <button
                            type="button"
                            onClick={prevStep}
                            className={`flex-1 py-4 rounded-2xl font-black uppercase tracking-widest transition-all ${
                                currentStep === 0 ? 'opacity-0 pointer-events-none' : 'bg-white border-2 border-gray-200 text-gray-500 hover:border-[#f8485e]/40'
                            }`}
                        >
                            Atrás
                        </button>
                        
                        {currentStep < totalSteps - 1 ? (
                            <button
                                type="button"
                                onClick={nextStep}
                                disabled={!isStepValid(currentStep)}
                                className={`flex-1 py-4 rounded-2xl shadow-xl font-black uppercase tracking-widest transition-all active:scale-95 ${
                                    isStepValid(currentStep)
                                        ? 'bg-[#f8485e] hover:bg-[#d93a4f] text-white'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                                }`}
                            >
                                Siguiente ✨
                            </button>
                        ) : (
                            <button
                                type="submit"
                                disabled={!isStepValid(currentStep)}
                                className={`flex-1 py-4 rounded-2xl shadow-xl font-black uppercase tracking-widest transition-all active:scale-95 ${
                                    isStepValid(currentStep)
                                        ? 'bg-[#f8485e] hover:bg-[#d93a4f] text-white'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                                }`}
                            >
                                Enviar 🚀
                            </button>
                        )}
                    </div>
                </form>
            </motion.div>
        </BackgroundLayout>
    );
};

export default SubstancePage;